import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { phanCongRepository } from '../../../lib/repositories/van-ban-phan-cong.repository';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import type { GhiNhiemVuTay, TienDoPhanCong, YeuCauPhanCongTep } from '../types-phan-cong';

/**
 * Hook của màn Phân công nhiệm vụ. Danh sách đọc một lần 200 dòng rồi lọc ở client (như bản cũ); ghi xong
 * chỉ vô hiệu khoá danh sách. Phân công từ tệp chạy nền ở backend: hook gửi tệp rồi tra
 * `GET /task-assignments/jobs/{job_id}` mỗi 4 giây (ADR-0071) tới khi xong, tối đa 2 phút như bản cũ.
 */
export const KHOA_PHAN_CONG = ['van-ban', 'phan-cong'] as const;
const NHIP_TRA = 4_000;
const CHO_TOI_DA = 120_000;

export function useNhiemVuPhanCong() {
  return useQuery({ queryKey: KHOA_PHAN_CONG, queryFn: phanCongRepository.danhSach });
}

/** Mở dialog sửa bằng dữ liệu mới nhất của một nhiệm vụ (`GET /task-assignments/{id}`). */
export function useDocNhiemVu() {
  const toast = useToast();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (id: string) => phanCongRepository.chiTiet(id),
    onError: (err) => toast.error(dichLoi(err)),
  });
}

/** Lỗi hiện trong dialog (`LoiForm`), không toast. */
export function useLuuNhiemVuTay() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: (v: { id: string | null; vao: GhiNhiemVuTay }) =>
      v.id ? phanCongRepository.sua(v.id, v.vao) : phanCongRepository.tao(v.vao),
    onSuccess: (_d, v) => {
      void queryClient.invalidateQueries({ queryKey: KHOA_PHAN_CONG });
      toast.success(t(v.id ? 'vanBanPhanCong.daCapNhat' : 'vanBanPhanCong.daThem'));
    },
  });
}

export function useXoaNhiemVuPhanCong() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (id: string) => phanCongRepository.xoa(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KHOA_PHAN_CONG });
      toast.success(t('vanBanPhanCong.daXoa'));
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}

/**
 * Mở tệp đính kèm ở tab mới hoặc tải về. Tab trống mở **ngay trong cú bấm** (mở sau `await` thì trình
 * chặn popup chặn), rồi trỏ sang URL của blob khi tải xong.
 */
export function useMoTepNhiemVu() {
  const toast = useToast();
  const dichLoi = useLoi();
  return useCallback(
    async (id: string, tenTep: string, cach: 'xem' | 'tai') => {
      const tab = cach === 'xem' ? window.open('', '_blank') : null;
      try {
        const blob = ganMime(await phanCongRepository.taiTep(id), tenTep);
        const url = URL.createObjectURL(blob);
        if (cach === 'xem') {
          if (tab) tab.location.href = url;
          else window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = tenTep;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err) {
        tab?.close();
        toast.error(dichLoi(err));
      }
    },
    [toast, dichLoi],
  );
}

const MIME_THEO_DUOI: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  txt: 'text/plain; charset=utf-8',
  md: 'text/plain; charset=utf-8',
  csv: 'text/csv; charset=utf-8',
};

/** Server trả `octet-stream` thì gán lại MIME theo đuôi để tab xem trước hiển thị đúng. */
function ganMime(blob: Blob, tenTep: string): Blob {
  if (blob.type && blob.type !== 'application/octet-stream') return blob;
  const mime = MIME_THEO_DUOI[tenTep.split('.').pop()?.toLowerCase() ?? ''];
  return mime ? blob.slice(0, blob.size, mime) : blob;
}

/** Trích đoạn nội dung tệp để xem trước trong dialog upload; lỗi thì im (backend sẽ đọc lại khi phân công). */
export function useTrichChuTep() {
  return useMutation({ mutationFn: (tep: File) => phanCongRepository.trichChu(tep) });
}

export type BuocPhanCong = 'tai-len' | 'ai-dang-xu-ly' | null;

/**
 * Gửi tệp cho AI phân công rồi chờ tác vụ nền xong. `huy()` dừng vòng tra (đóng dialog, rời trang); tác vụ
 * nền ở backend vẫn chạy tiếp và nhiệm vụ tạo ra sẽ hiện ở lần tải danh sách sau.
 */
export function usePhanCongTuTep() {
  const queryClient = useQueryClient();
  const huyRef = useRef(false);
  const [buoc, setBuoc] = useState<BuocPhanCong>(null);

  const mutation = useMutation({
    mutationFn: async (vao: YeuCauPhanCongTep): Promise<TienDoPhanCong | 'het-gio' | 'da-huy'> => {
      huyRef.current = false;
      setBuoc('tai-len');
      const { job_id } = await phanCongRepository.phanCongTuTep(vao);
      setBuoc('ai-dang-xu-ly');
      const batDau = Date.now();
      while (Date.now() - batDau < CHO_TOI_DA) {
        await new Promise((r) => window.setTimeout(r, NHIP_TRA));
        if (huyRef.current) return 'da-huy';
        const s = await phanCongRepository.tienDo(job_id);
        if (huyRef.current) return 'da-huy';
        if (s.done) return s;
      }
      return 'het-gio';
    },
    onSettled: (kq) => {
      setBuoc(null);
      if (kq && kq !== 'da-huy') void queryClient.invalidateQueries({ queryKey: KHOA_PHAN_CONG });
    },
  });

  const huy = useCallback(() => {
    huyRef.current = true;
    setBuoc(null);
    mutation.reset();
  }, [mutation]);

  return { ...mutation, buoc, huy };
}

